/**
 * In-app destinations an `open-tool` notification CTA can point at.
 *
 * Feature adoption: an announcement's CTA should land the client INSIDE the
 * feature, so the admin picks a destination rather than typing a URL. The picker
 * exists because a free-text path is the one part of this flow an admin can get
 * silently wrong — a typo'd route renders a button that navigates to a 404, and
 * nothing in the authoring flow would catch it.
 *
 * Paths mirror `legal-opinion-frontend/src/constants/routes.ts` (the ToolRoute
 * enum). There is no build-time link between the two repos, so this list is
 * curated rather than exhaustive: it carries the tools we actually announce.
 * Anything else can still be entered by hand via the "Custom path" option.
 */

export interface NotificationToolRoute {
  /** Path passed as the CTA's url, e.g. '/titan-personal-roa'. */
  path: string;
  /** What the admin sees in the picker. */
  label: string;
  /** Grouping in the dropdown; mirrors how the tools are grouped in-app. */
  group: 'Broker AI' | 'Intelligent Automation' | 'Account';
}

export const NOTIFICATION_TOOL_ROUTES: readonly NotificationToolRoute[] = [
  // Broker AI (Titan) tools
  { path: '/titan-personal-roa', label: 'Personal ROA', group: 'Broker AI' },
  { path: '/titan-personal-rar', label: 'Personal RAR', group: 'Broker AI' },
  { path: '/titan-commercial-roa', label: 'Commercial ROA', group: 'Broker AI' },
  { path: '/titan-commercial-renewal-roa', label: 'Commercial Renewal ROA', group: 'Broker AI' },
  { path: '/titan-personal-renewal-roa', label: 'Personal Renewal ROA', group: 'Broker AI' },
  { path: '/titan-life-quote-comparison', label: 'Life Quote Comparison', group: 'Broker AI' },
  { path: '/investment-onboarding-roa', label: 'Investment Onboarding ROA', group: 'Broker AI' },
  { path: '/life-onboarding', label: 'Life Onboarding', group: 'Broker AI' },
  { path: '/client-minutes-broker', label: 'Client Minutes', group: 'Broker AI' },
  { path: '/heavy-haulage-confirmation', label: 'Heavy Haulage Confirmation', group: 'Broker AI' },

  // Intelligent Automation tools
  { path: '/memo', label: 'Memo', group: 'Intelligent Automation' },
  { path: '/policy-analysis', label: 'Policy Analysis', group: 'Intelligent Automation' },
  { path: '/quantum-damages', label: 'Quantum Damages', group: 'Intelligent Automation' },
  { path: '/report', label: 'Report', group: 'Intelligent Automation' },
  { path: '/pdf-combiner', label: 'PDF Combiner', group: 'Intelligent Automation' },

  // Account surfaces
  { path: '/dashboard', label: 'Dashboard', group: 'Account' },
  { path: '/manage-subscription', label: 'Manage Subscription', group: 'Account' },
  { path: '/organization', label: 'Organization Settings', group: 'Account' },
] as const;

/**
 * The ONLY shape an `open-tool` CTA url may take — mirrors the backend's
 * SAFE_RELATIVE_PATH and the frontend renderer's isSafeRelativePath. Kept
 * strict so a hand-typed custom path can't become an off-site redirect: no
 * scheme, no host, no traversal, no query/fragment.
 */
export const SAFE_RELATIVE_PATH = /^\/[a-z0-9-]+(\/[a-z0-9-]+)*$/;

export const isSafeRelativePath = (url?: string): boolean => !!url && SAFE_RELATIVE_PATH.test(url);

/** True when the path is not one of the curated options (so the UI shows the free-text field). */
export const isCustomToolRoute = (url?: string): boolean =>
  !!url && !NOTIFICATION_TOOL_ROUTES.some((r) => r.path === url);

/**
 * The routes a given admin build may offer.
 *
 * A broker admin must not be handed the Intelligent Automation tool list: those
 * routes don't exist for their clients, so a CTA pointing at one 404s for every
 * recipient — precisely the failure the picker exists to prevent. (It also
 * discloses the other tenant's tool line to an external partner.) Account
 * surfaces exist on both stacks and stay shared.
 */
export const visibleToolRoutes = (isBrokerAdmin: boolean): NotificationToolRoute[] =>
  NOTIFICATION_TOOL_ROUTES.filter(
    (r) => r.group === 'Account' || r.group === (isBrokerAdmin ? 'Broker AI' : 'Intelligent Automation'),
  );
